import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, concatMap, Observable, throwError} from "rxjs";
import {APIResponse} from "../../../models/Response";
import {
    Attendee,
    AttendeeWithImage,
    CondensedMeeting,
    ExpendedViewResponse,
    ExtendedAttendee,
    GetNotificationsRequestBody,
    IsTranscriptionEnum,
    Meeting,
    Meeting_Transcription_Model,
    MeetingActionItemsModel, MeetingFileMetaData,
    MeetingFiltersSelectChange,
    MeetingNotesModel, TaskDetailsModel, text_file_extensions,
    UpdateAttendeeResponseRequestBody,
    UploadFileForModelResponse,
    UploadFileMetadataRequest
} from "../models/meeting.model";
import {catchError, map, timeout} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {SharedService} from "../../../services/shared.service";
import {UserDetails} from "../../../models/userDetails";
import {UserAccount} from "../../../models/custom-models/user-account";
import {format} from "date-fns";
import {HandleErrorService} from "../../../services/handle-error-service";
import {ToastrService} from "ngx-toastr";
import {UserPreferencesService} from "../../shared/Services/user-preferences.service";
import {DashboardService} from "../../../components/dashboard/Services/dashboard.service";

@Injectable({
    providedIn: "root",
})
export class MeetingsService {
    fromDate: any;
    toDate: any;
    apiServerUrl = environment.apiBaseUrl;
    aiServerUrl = environment.aiServerUrlForMeetingTranscription;
    teamMembers: UserDetails[] = [];
    currentUserAccountIds: number[] = [];
    requestBodyToGetCondensedViewMeetings: GetNotificationsRequestBody;
    private allTeamMembers = new BehaviorSubject<UserDetails[]>([]);
    allTeamMembers$ = this.allTeamMembers.asObservable();
    private expandedViewMeetings: BehaviorSubject<Meeting[]> = new BehaviorSubject<Meeting[]>([]);
    expandedViewMeetings$ = this.expandedViewMeetings.asObservable();
    private newEditableMeetings = new BehaviorSubject<CondensedMeeting[]>([]);
    newEditableMeetings$ = this.newEditableMeetings.asObservable();
    private filterChangedEvent: BehaviorSubject<MeetingFiltersSelectChange | null> =
        new BehaviorSubject<MeetingFiltersSelectChange | null>(null);
    filterChangedEvent$ = this.filterChangedEvent.asObservable();
    private selectedMeetingCardEvent: BehaviorSubject<Meeting | null> =
        new BehaviorSubject<Meeting | null>(null);
    selectedMeetingCardEvent$ = this.selectedMeetingCardEvent.asObservable();

    constructor(
        private http: HttpClient,
        private sharedService: SharedService,
        private handleErrorService: HandleErrorService,
        private toastrService: ToastrService,
        private userPreferenceService: UserPreferencesService,
        private dashboardService: DashboardService
    ) {
        this.setCurrentUserAccountIds();
    }
    setCurrentUserAccountIds() {
        const userAccounts = this.sharedService.getUserAccounts();
        if (userAccounts) {
            this.currentUserAccountIds = userAccounts.map(
                (userAccount: UserAccount) => {
                    return userAccount.accountId;
                }
            );
        } else {
            this.currentUserAccountIds = [];
        }
    }

    // passing the updated meeting data with a new reference so even if distinct until is used the component reading it will treat it a new reference
    updateExpandedViewMeeting(updatedMeeting: Meeting) {
        const currentMeetings = this.expandedViewMeetings.value || [];
        const exists = currentMeetings.some(m => m.meetingNumber === updatedMeeting.meetingNumber);
        const updatedMeetings = exists
            ? currentMeetings.map(m =>
                m.meetingNumber === updatedMeeting.meetingNumber
                    ? { ...m, ...updatedMeeting }
                    : m
            )
            : [...currentMeetings, updatedMeeting];  // add a new meeting if not present
        this.expandedViewMeetings.next(updatedMeetings);
    }

    getFilterChangeEventObservable() {
        return this.filterChangedEvent.value;
    }

    setFilterChangeEventObservable(filterChangeEvent: MeetingFiltersSelectChange | null) {
        this.filterChangedEvent.next(filterChangeEvent);
    }

    setTeamMembers(teamMembers: UserDetails[]) {
        this.teamMembers = teamMembers;
        this.allTeamMembers.next(teamMembers);
    }

    getMemberByAccountId(accountId: number): UserDetails | undefined {
        return this.teamMembers.find(member => member.accountId == accountId.toString());
    }

    getTeamMembers() {
        return this.teamMembers;
    }

    updateAttendeeResponse(attendeeResponse: UpdateAttendeeResponseRequestBody, accountId: string) {
        const headers = {
            accountIds: accountId,
        };
        return this.http
            .post<APIResponse<any>>(`${this.apiServerUrl}/meeting/updateAttendeeResponse`, attendeeResponse, {
                headers: headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    // Optimized method with proper typing and performance improvements
    getMeetingAttendeeList(meeting: Meeting): AttendeeWithImage[] {
        if (!meeting.attendeeRequestList?.length || !this.teamMembers.length) {
            return [];
        }

        // Create a Map for O(1) lookups instead of O(n) find operations
        const teamMembersMap = new Map(
            this.teamMembers.map(member => [Number(member.accountId), member])
        );

        return meeting.attendeeRequestList
            .map(attendee => {
                const member = teamMembersMap.get(attendee.accountId);
                if (!member) return null;

                return {
                    accountId: attendee.accountId,
                    attendeeName: `${member.firstName} ${member.lastName}`.trim(),
                    attendeeEmail: member.email ?? '',
                    attendeeImage: ''
                } as AttendeeWithImage;
            })
            .filter((attendee): attendee is AttendeeWithImage => attendee !== null);
    }

    getAllAttendees(meeting: Meeting): Attendee[] {
        if (meeting.attendeeList && meeting.attendeeList.length > 0) {
            return meeting.attendeeList;
        } else if (meeting.attendeeRequestList && meeting.attendeeRequestList.length > 0) {
            return meeting.attendeeRequestList;
        } else {
            return [];
        }
    }

    getOrganizerName(organizerAccountId: number): string {
        let organizerName = "";
        this.teamMembers.forEach((member: UserDetails) => {
            if (organizerAccountId == Number(member.accountId)) {
                organizerName = member.firstName + " " + member.lastName;
            }
        });
        return organizerName;
    }

    getAllExpendedViewMeetings(
        requestBody: GetNotificationsRequestBody,
    ): Observable<APIResponse<ExpendedViewResponse>> {
        // this.resetExpandedViewMeetings();
        this.expandedViewMeetings.next([]);
        const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser(true) as string;
        const headers = {
            accountIds: accountIds,
        };
        return this.http
            .post<APIResponse<ExpendedViewResponse>>(
                `${this.apiServerUrl}/meeting/getMeetingExpandedView`,
                requestBody,
                { headers },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
                map((response: APIResponse<ExpendedViewResponse>) => {
                    if (response.data.meetingResponses.length > 0) {
                        // filter cancelled meetings
                        response.data.meetingResponses = response.data.meetingResponses
                            .filter((meeting: Meeting) => meeting.isCancelled !== true)
                            .map((meeting: any) => {
                                return {
                                    ...meeting,
                                    modelFetchResponseList: meeting.modelFetchedList ?? meeting.modelFetchResponseList,
                                    viewTranscription: meeting.viewTranscription ?? false,
                                } as Meeting;
                            });
                    }
                    this.expandedViewMeetings.next(response.data.meetingResponses);
                    return response;
                }),
            );
    }

    getFromToDateOfMonth(date: Date) {
        let firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        let lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const fromDateFormat = "yyyy-MM-dd'T'00:00:00";
        const toDateFormat = "yyyy-MM-dd'T'23:59:59";
        const firstDayOfMonthString = format(firstDay, fromDateFormat);
        const lastDayOfMonthString = format(lastDay, toDateFormat);
        return { fromDate: firstDayOfMonthString, toDate: lastDayOfMonthString };
    }

    getAllMeetingCondensedView(requestBody: GetNotificationsRequestBody): Observable<APIResponse<CondensedMeeting[]>> {
        // reset meetings list first
        this.newEditableMeetings.next([]);
        const accountIds = this.sharedService.getAllAccountIdsOfLoggedInUser(true) as string;
        const headers = {
            accountIds: accountIds,
        };
        return this.http
            .post<APIResponse<CondensedMeeting[]>>(
                `${this.apiServerUrl}/meeting/getMeetingCondensedView`,
                requestBody,
                { headers },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
                map((response: APIResponse<CondensedMeeting[]>) => {
                    if (response.data.length > 0) {
                        //     filter cancelled meetings
                        response.data = response.data.filter((meeting: CondensedMeeting) => {
                            return meeting.isCancelled !== true;
                        });
                    }
                    this.newEditableMeetings.next(response.data);
                    return response;
                }),
            );
    }

    getRefreshedMeetingForCondensedView() {
        this.sharedService.showSpinner();
        let requestBody = new GetNotificationsRequestBody();
        requestBody.fromDate = this.fromDate;
        requestBody.toDate = this.toDate;
        if(!this.fromDate && !this.toDate){
            this.sharedService.hideSpinner();
        }else{
            this.getAllExpendedViewMeetings(requestBody).subscribe(
                (response: any) => {
                    this.sharedService.hideSpinner();
                },
                (error: any) => {
                    this.sharedService.hideSpinner();
                    this.handleErrorService.handleError(error);
                },
            );
        }
    }

    setFromDateAndToDate(fromDate: string, toDate: string) {
        this.fromDate = fromDate;
        this.toDate = toDate;
    }

    emitFilterChangerEvent(selectedOrg: number | null | undefined, selectedTeam: number| null | undefined, selectedProj: number| null | undefined, selectedBu: number| null | undefined) {
        let filter = new MeetingFiltersSelectChange();
        filter.orgId = selectedOrg;
        filter.teamId = selectedTeam;
        filter.projectId = selectedProj;
        filter.buId = selectedBu;
        this.filterChangedEvent.next(filter);
    }

    isUserAllowedToRespond(meeting: Meeting): boolean {
        // this.setCurrentUserAccountIds();
        let isAllowed = false;
        let attendeeList = this.getAllAttendees(meeting);
        if (attendeeList.length > 0 && this.currentUserAccountIds.length > 0) {
            this.currentUserAccountIds.forEach((accountId: number) => {
                for (let attendee of attendeeList) {
                    if (attendee.accountId === accountId) {
                        isAllowed = true;
                        break;
                    }
                }
            });
        }
        return isAllowed;
    }

    isUserHavePermissionsToEdit(meeting: CondensedMeeting) {
        let isAllowed = false;
        this.currentUserAccountIds.forEach((accountId: number) => {
            if (accountId === meeting.organizerAccountId || accountId === meeting.createdAccountId) {
                isAllowed = true;
            }
        });
        return isAllowed;
    }

    getAllRecordedEfforts(meeting: Meeting, allUsers: UserDetails[]) {
        if (!allUsers || allUsers.length == 0) {
            return null;
        }
        let allRecordedEfforts: ExtendedAttendee[] = [];
        // allUsers.find((user:userDetails) => Number(user.accountId) === currentObjAccountId)
        meeting.attendeeRequestList?.forEach((attendee: Attendee) => {
            if (!attendee.attendeeDuration || attendee.attendeeDuration == 0) {
                return;
            }
            let user = allUsers.find((user: UserDetails) => Number(user.accountId) === attendee.accountId);
            if (user) {
                let extendedAttendee: ExtendedAttendee = new ExtendedAttendee();
                extendedAttendee.accountId = attendee.accountId;
                extendedAttendee.fullName = user.firstName + " " + user.lastName;
                extendedAttendee.email = user.email;
                extendedAttendee.attendeeDuration = attendee.attendeeDuration;
                extendedAttendee.didYouAttend = attendee.didYouAttend;
                extendedAttendee.attendeeId = attendee.attendeeId;
                extendedAttendee.isAttendeeExpected = attendee.isAttendeeExpected;
                extendedAttendee.reminderAttendeeExpectedResponse = attendee.reminderAttendeeExpectedResponse;
                extendedAttendee.attendeeLogId = attendee.attendeeLogId;
                allRecordedEfforts.push(extendedAttendee);
            }
        });
        return allRecordedEfforts;
    }

    getCurrentUserAttendeeRequestList(currentValue: Meeting): Attendee | null {
        let currentUserAttendeeRequestList: Attendee | null = null;
        if (currentValue.attendeeRequestList) {
            currentValue.attendeeRequestList?.forEach((attendee) => {
                if (this.currentUserAccountIds.includes(attendee.accountId)) {
                    currentUserAttendeeRequestList = attendee;
                }
            });
        } else {
            currentUserAttendeeRequestList = null;
        }
        return currentUserAttendeeRequestList;
    }

    removeMeetingFromExpandedView(meeting: Meeting) {
        let meetings = this.expandedViewMeetings.value;
        meetings = meetings.filter((m) => {
            return m.meetingNumber !== meeting.meetingNumber;
        });
        this.expandedViewMeetings.next(meetings);
    }

    addMeetingToExpandedView(meeting: Meeting) {
        let meetings = this.expandedViewMeetings.value;
        meetings.push(meeting);
        this.expandedViewMeetings.next(meetings);
    }

    resetExpandedViewMeetings() {
        this.expandedViewMeetings.next([]);
    }

    postTranscribeMeetingData(
        meetingNumber: number,
        orgId: number,
        file: File,
        is_transcription: number,
        model: string,
        accountId: string,
        duration: number = 0
    ) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("meeting_id", meetingNumber.toString());
        formData.append("org_id", orgId.toString());
        formData.append("is_transcription", is_transcription.toString());
        formData.append("model", model);
        formData.append("file_name", file.name);
        formData.append("file_ext", file.name.split(".").pop() || "");
        formData.append("file_size", `${Math.round(file.size / 1024)} kb`);
        formData.append("account_id", accountId);
        formData.append("duration", duration.toString());

        return this.http
            .post<APIResponse<any>>(`${this.aiServerUrl}/transcribe_meeting`, formData, {
                headers: {
                    // timeout: "false",
                    // secretKey: environment.aiServerSecretKeyForLocal
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    uploadFileMetadataForModel(
        requestData: UploadFileMetadataRequest,
        accountId: string
    ): Observable<APIResponse<UploadFileForModelResponse>> {
        const headers = {
            'accountIds': accountId,
            'Content-Type': 'application/json'
        };

        return this.http
            .post<APIResponse<UploadFileForModelResponse>>(
                `${this.apiServerUrl}/meeting/uploadFileMetadataForModel`,
                requestData,
                { headers }
            )
            .pipe(
                catchError((error) => {
                    console.error('Upload file metadata error:', error);
                    return throwError(() => error);
                })
            );
    }

    postFetchTranscriptionResults(meetingId: number, orgId: number, useLlama: boolean, useChatgpt: boolean) {
        const formData = new FormData();
        formData.append("meeting_id", String(meetingId));
        formData.append("org_id", String(orgId));

        // Handle model selection based on API
        if (useLlama && useChatgpt) {
            formData.append("model", "all");
        } else if (useLlama) {
            formData.append("model", "llama");
        } else if (useChatgpt) {
            formData.append("model", "chatgpt");
        }

        return this.http.post<APIResponse<any>>(`${this.aiServerUrl}/fetch_trans_results`, formData, {
            headers: {
                // 'timeout': 'false',
                 // 'secretKey': environment.aiServerSecretKeyForLocal
            }
        })
            .pipe(
                timeout(30000),
                catchError((error) => {
                    console.error('API Error:', error);
                    return throwError(() => error);
                })
            );
    }

    postFetchTranscription(meetingId: number, orgId: number, selectedModel: string = Meeting_Transcription_Model.LLAMA) {
        const formData = new FormData();
        formData.append("meeting_id", String(meetingId));
        formData.append("org_id", String(orgId));

        if (selectedModel === Meeting_Transcription_Model.BOTH) {
            formData.append("model", "all");
        } else if (selectedModel === Meeting_Transcription_Model.CHATGPT) {
            formData.append("model", "chatgpt");
        } else if (selectedModel === Meeting_Transcription_Model.LLAMA) {
            formData.append("model", "llama");
        }

        return this.http.post<APIResponse<any>>(`${this.aiServerUrl}/fetch_transcription`, formData, {
            headers: {
                // secretKey: environment.aiServerSecretKeyForLocal
            }
        })
            .pipe(
                catchError((error) => {
                    console.error('Fetch transcription error:', error);
                    return throwError(() => error);
                })
            );
    }

    highlightMeetingInList(meetingToRedirectBasedOnNavigation: Meeting) {
        let idToFind = meetingToRedirectBasedOnNavigation.meetingNumber;
        if (!idToFind) {
            return;
        }
        requestAnimationFrame(() => {
            const element = document.getElementById(idToFind!);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
                element.style.backgroundColor = "#f5f5f5";
                element.style.color = "black";
                element.style.transition = "background-color 3s";
                element.style.transitionTimingFunction = "ease";
                setTimeout(() => {
                    element.style.backgroundColor = "";
                    element.style.color = "";
                }, 5000);
            }
        });
    }
    emitSelectedMeetingCardEvent(card: Meeting | null){
        this.selectedMeetingCardEvent.next(card);
    }

    processTranscriptionAndUploadAnalysis(
        meetingId: number,
        orgId: number,
        accountId: string,
        analysisData: MeetingAnalysisDetailsRequest,
        model: string
    ): Observable<APIResponse<MeetingAnalysisResponse>> {
        const headers = {
            'accountIds': accountId,
            'Content-Type': 'application/json'
        };

        return this.http
            .post<APIResponse<MeetingAnalysisResponse>>(
                `${this.apiServerUrl}/meeting/uploadMeetingAnalysis`,
                analysisData,
                { headers }
            )
            .pipe(
                catchError((error) => {
                    console.error('Upload meeting analysis error:', error);
                    return throwError(() => error);
                })
            );
    }

    private getUserAccountId() {
        let accountId: null | number = null;
        let accountIdByOrg = this.dashboardService.getOrgIdOfSelectedEntity();
        if(accountIdByOrg){
            accountId = this.sharedService.getUserAccountIdWithOrgId(accountIdByOrg);
            if(accountId){
                return accountId;
            }
        }
        let userPreferredOrgId = this.userPreferenceService.getUserPreferredOrgId();
        if(userPreferredOrgId){
            accountId = this.sharedService.getUserAccountIdWithOrgId(userPreferredOrgId);
            if(accountId){
                return accountId;
            }
        }
        let allAccountIds = this.sharedService.getAllAccountIdsOfLoggedInUser();
        if(allAccountIds){
            let allAccountIdsArray = (allAccountIds as string).split(",");
            if(allAccountIdsArray.length > 0){
                return Number(allAccountIdsArray[0]);
            }
        }
        return -1;
    }

    uploadAndAnalyzeFile(
        meetingId: number,
        orgId: number,
        selectedFile: File,
        aiModelToUserForTranscription: string,
        duration: number = 0
    ): Observable<APIResponse<MeetingAnalysisResponse>> {
        const fileExtension = selectedFile.name.split(".").pop() || "";
        let is_Transcription: number;
        if (fileExtension && fileExtension.toLowerCase() === "text" || text_file_extensions.includes(fileExtension.toLowerCase())) {
            is_Transcription = IsTranscriptionEnum.TEXT_FILE;
        } else {
            is_Transcription = IsTranscriptionEnum.AUDIO_FILE;
        }
        const model = aiModelToUserForTranscription;
        const getModelIdFromSelection = (modelString: string): number => {
            switch (modelString) {
                case Meeting_Transcription_Model.LLAMA:
                    return 1;
                case Meeting_Transcription_Model.CHATGPT:
                    return 2;
                case Meeting_Transcription_Model.BOTH:
                    return 3;
                default:
                    return 1;
            }
        };

        const selectedModelId = getModelIdFromSelection(aiModelToUserForTranscription);
        const accountId = this.sharedService.getUserAccountIdWithOrgId(orgId)?.toString() ||
            this.userPreferenceService.getUserPreferredTeamId()?.toString() ||
            (this.sharedService.getAllAccountIdsOfLoggedInUser()[0] as string).split(",")[0];

        const fileMetadata: MeetingFileMetaData = {
            fileName: selectedFile.name,
            fileExtension: fileExtension,
            fileSize: selectedFile.size
        };

        // RxJS pipe that chains the two API calls.
        return this.postTranscribeMeetingData(
            meetingId,
            orgId,
            selectedFile,
            is_Transcription,
            model,
            accountId,
            duration
        ).pipe(
            concatMap(() => {
                const accountId = this.sharedService.getUserAccountIdWithOrgId(orgId)?.toString() ||
                    this.userPreferenceService.getUserPreferredTeamId()?.toString() ||
                    (this.sharedService.getAllAccountIdsOfLoggedInUser()[0] as string).split(",")[0];
                const uploadFileMetadataRequest: UploadFileMetadataRequest = {
                    meetingId: meetingId,
                    orgId: orgId,
                    modelId: selectedModelId,
                    meetingFileMetaDataList: [fileMetadata],
                    uploadedDateTime: new Date().toISOString()
                };
                return this.uploadFileMetadataForModel(uploadFileMetadataRequest, accountId);
            }),
            concatMap((metadataRes: APIResponse<UploadFileForModelResponse>) => {
                const accountId = this.sharedService.getUserAccountIdWithOrgId(orgId)?.toString() ?? "";
                const analysisData: MeetingAnalysisDetailsRequest = {
                    meetingId: meetingId,
                    modelId: metadataRes.data.modelId,
                    actionItemList: [],
                    meetingNoteList: [],
                    minutesOfMeeting: null,
                    meetingFileMetaDataList: metadataRes.data.meetingFileMetaDataList,
                };

                return this.processTranscriptionAndUploadAnalysis(
                    meetingId,
                    orgId,
                    accountId,
                    analysisData,
                    aiModelToUserForTranscription
                );
            })
        );
    }

    prepareActionItemsForAnalysis(
        actionItems: MeetingActionItemsModel[],
        currentUserAccountId: number
    ): ActionItemAnalysisRequest[] {
        return actionItems.map(item => ({
            actionItemId: item.actionItemId || null,
            actionItem: item.actionItem,
            postedByAccountId: item.postedByAccountId || currentUserAccountId,
            modifiedByAccountId: item.modifiedByAccountId || currentUserAccountId,
            isDeleted: item.isDeleted || false,
            isImportant: item.isCritical || false,
            isUpdated: item.isUpdated || false,
            version: item.version,
            createdDateTime: item.createdDateTime || null,
            lastUpdatedDateTime: item.lastUpdatedDateTime || null,
            taskId: item.taskId || null,
        }));
    }

    convertAnalysisResponseToMeetingModels(response: MeetingAnalysisResponse): {
        notes: MeetingNotesModel[],
        actionItems: MeetingActionItemsModel[]
    } {
        let notes: MeetingNotesModel[] = (response.meetingNoteList ?? []).map(note => ({
            meetingNoteId: note.meetingNoteId,
            meetingNote: note.meetingNote,
            postedByAccountIdDetails: note.postedByAccountIdDetails.accountId,
            modifiedByAccountIdDetails: note.modifiedByAccountIdDetails?.accountId || null,
            isImportant: note.isImportant,
            isDeleted: note.isDeleted,
            version: note.version,
            createdDateTime: note.createdDateTime,
            updatedDateTime: note.updatedDateTime,
            isUpdated: note.isUpdated,
        }));

        let actionItems: MeetingActionItemsModel[] = (response.actionItemList ?? []).map(item => ({
            actionItemId: item.actionItemId,
            actionItem: item.actionItem,
            postedByAccountId: item.postedByAccountId,
            modifiedByAccountId: item.modifiedByAccountId || null,
            createdDateTime: item.createdDateTime || null,
            lastUpdatedDateTime: item.lastUpdatedDateTime || null,
            version: item.version,
            isDeleted: item.isDeleted || false,
            isUpdated: item.isUpdated || false,
            isCritical: item.isImportant,
            taskId: item.taskId || null,
            taskDetails: item.taskDetails || null
        }));

        return { notes, actionItems };
    }

    prepareMeetingNotesForAnalysis(
        meetingNotes: MeetingNotesModel[]
    ): MeetingNoteAnalysisRequest[] {
        return meetingNotes.map(note => ({
            meetingNoteId: note.meetingNoteId || null,
            meetingNote: note.meetingNote,
            isImportant: note.isImportant || false,
            isDeleted: note.isDeleted || false,
            isUpdated: note.isUpdated || false
        }));
    }
}

export interface MeetingAnalysisDetailsRequest {
    meetingId: number;
    modelId?: number | null;
    actionItemList?: ActionItemAnalysisRequest[];
    meetingNoteList?: MeetingNoteAnalysisRequest[];
    minutesOfMeeting?: string | null;
    meetingFileMetaDataList?: MeetingFileMetaData[];
}

export interface ActionItemAnalysisRequest {
    actionItemId?: number | null;
    actionItem: string;
    postedByAccountId: number;
    modifiedByAccountId?: number | null;
    isDeleted?: boolean | null;
    isImportant?: boolean;
    isUpdated?: boolean;
    version?: number | null;
    createdDateTime?: string | null;
    lastUpdatedDateTime?: string | null;
}

export interface MeetingNoteAnalysisRequest {
    meetingNoteId?: number | null;
    meetingNote: string;
    isImportant: boolean;
    isDeleted: boolean;
    isUpdated: boolean;
}

export interface MeetingAnalysisResponse {
    meetingId: number;
    modelId?: number | null;
    actionItemList: ActionItemAnalysisResponse[];
    meetingNoteList: MeetingNoteAnalysisResponse[];
    minutesOfMeeting?: string | null;
}

export interface ActionItemAnalysisResponse {
    isImportant: boolean;
    actionItemId: number;
    actionItem: string;
    postedByAccountId: number;
    modifiedByAccountId?: number | null;
    createdDateTime?: string | null;
    lastUpdatedDateTime?: string | null;
    version: number;
    isDeleted?: boolean | null;
    isUpdated?: boolean | null;
    taskId?: number | null;
    taskDetails?: TaskDetailsModel | null;
}

export interface MeetingNoteAnalysisResponse {
    isUpdated: boolean;
    meetingNoteId: number;
    meetingNote: string;
    postedByAccountIdDetails: {
        email: string;
        accountId: number;
        firstName: string;
        lastName: string;
        isActive: boolean;
    };
    modifiedByAccountIdDetails?: {
        email: string;
        accountId: number;
        firstName: string;
        lastName: string;
        isActive: boolean;
    } | null;
    isImportant: boolean;
    isDeleted: boolean;
    version: number;
    createdDateTime: string;
    updatedDateTime: string;
}